/**
 * App color palettes (moved verbatim from the retired Redux theme slice).
 * `colors` is fully derived from the light/dark `Scheme` — there is no other
 * theme state. React code reads colors via styled-components' `useTheme()`;
 * non-React code (chart configs, audit renderers) uses `getCurrentColors()`.
 */

export type Scheme = 'light' | 'dark';

export type ColorSet = {
  gp1: string;
  gp2: string;
  gradient: string;
  gradientVertical: string;
  gradientBlue: string;
  hoverGradient: string;
  btnWhite: string;
  prussianBlue: string;
  davysGrey: string;
  aliceBlue: string;
  brightBlue: string;
  cultured: string;
  stroke: string;
  cflowerBlue: string;
  white: string;
  warning: string;
  error: string;
  compareAFill: string;
  compareBLine: string;
  compareBFill: string;
  success: string;
  bgGray: string;
  compareA: string;
  compareB: string;
  shadow: string;
  shadowLight: string;
  skeletonBase: string;
  skeletonHighlight: string;
  dropZoneErrBg: string;
  dropZoneErrBorder: string;
  statusGreen: string;
  statusAmber: string;
  statusRed: string;
  statusOrange: string;
  // Org hierarchy avatar colors
  orgAvatarRoot: string;
  orgAvatarParent: string;
  orgAvatarSub: string;
  orgAvatarWorkspace: string;
  // Org table child row highlight
  childRowHighlight: string;
  // Card system (CRPD-5424 / design 17757-4640). The Figma tokens below have no
  // pre-existing equivalent; `stroke`, `cflowerBlue` and `prussianBlue` already match
  // their Figma counterparts exactly and are reused rather than duplicated here.
  borderDefault: string;
  borderSubtle: string;
  surfaceSubtle: string;
  textMuted: string;
  textStrong: string;
  textSecondary: string;
  textPlaceholder: string;
  sourceAccent: string;
  /** Full-strength modal/drawer scrim. Carries its own alpha; composite it at opacity 1. */
  scrim: string;
  // Content-pack status chips (partner / published). Added by task 001's round-2 review: these six
  // values were hard-coded hex literals in `ContentManagementPage.styles.ts`, so the chips kept
  // their light-mode colours on a dark card.
  chipPartnerBorder: string;
  chipPartnerBg: string;
  chipPartnerText: string;
  chipPublishedBorder: string;
  chipPublishedBg: string;
  chipPublishedText: string;
};

export type ColorName = keyof ColorSet;

const colorLib = {
  // ===== LIGHT =====
  gp1: '#E62E78', // #E62E78
  gp2: '#FD5630', // #FD5630
  gradient: 'linear-gradient(106.11deg, #E62E78 0.01%, #FD5630 98.53%)',
  gradientVertical: 'linear-gradient(180deg, #E62E78 0.01%, #FD5630 98.53%)',
  gradientBlue: 'linear-gradient(150.75deg, #09C1F4 -10.2%, #6A7590 121.37%)',
  hoverGradient: 'linear-gradient(106.11deg, #F26CA2 0.01%, #F97253 98.53%)',
  btnWhite: '#fff', // #ffffff
  prussianBlue: '#022033', // #022033
  davysGrey: '#555862', // #555862
  aliceBlue: '#D5E7F2', // #D5E7F2
  brightBlue: '#2BBDEF', // #2BBDEF
  cultured: '#F6F6F9', // #F6F6F9
  stroke: '#E9ECFF', // #E9ECFF
  cflowerBlue: '#6A7590', // #6A7590
  white: '#FFFFFF', // #FFFFFF
  warning: '#FF5A28', // #FF5A28
  error: '#E32121', // #E32121
  compareAFill: 'rgba(228, 0, 69, 0.2)',
  compareBLine: '#6B78CD', // #6B78CD
  compareBFill: 'rgba(107, 120, 205,0.2)',
  success: '#4caf50', // #4caf50
  bgGray: '#F3F4F6', // #F3F4F6
  compareA: '#18C372', // #18C372
  compareB: '#8000FF', // #8000FF
  skeletonBase: 'rgb(232, 233, 239)',
  skeletonHighlight: '#F6F6F9', // #F6F6F9
  dropZoneErrBg: '#ffeded', // #ffeded
  dropZoneErrBorder: '#ffcfcf', // #ffcfcf
  statusGreen: '#22A588', // #22A588
  statusAmber: '#FFA800', // #FFA800
  statusRed: '#E13D3D', // #E13D3D
  statusOrange: '#ED9C00', // #ED9C00 — used for "report building" state

  // ===== DARK =====
  gp1Dark: '#E62E78', // #E62E78
  gp2Dark: '#FD5630', // #FD5630
  gradientDark: 'linear-gradient(106.11deg, #E62E78 0.01%, #FD5630 98.53%)',
  gradientVerticalDark: 'linear-gradient(180deg, #E62E78 0.01%, #FD5630 98.53%)',
  gradientBlueDark: 'linear-gradient(150.75deg, #05779A -10.2%, #444A5B 121.37%)',
  hoverGradientDark: 'linear-gradient(106.11deg, #F26CA2 0.01%, #F97253 98.53%)',
  btnWhiteDark: '#F4F4F4', // #F4F4F4
  prussianBlueDark: '#b6c8d7', // #b6c8d7
  davysGreyDark: '#8B909C', // new #8B909C
  aliceBlueDark: '#2A3440', // #2A3440
  brightBlueDark: '#1E90A8', // #1E90A8
  culturedDark: '#191A1D', // #191A1D
  strokeDark: '#444B66', // #444B66
  cflowerBlueDark: '#79879f', // #79879F
  whiteDark: '#29292a', // #29292A
  warningDark: '#D14A20', // #D14A20
  errorDark: '#DD6F69', // #DD6F69

  compareAFillDark: 'rgba(155, 0, 49, 0.2)',
  compareADark: '#13A361', // #13A361
  compareBLineDark: '#545E9E', // #545E9E
  compareBFillDark: 'rgba(84, 94, 158, 0.2)',
  compareBDark: '#6A00D9', // #6A00D9
  successDark: '#3C8F41', // #3C8F41
  bgGrayDark: '#1F1F24', // #1F1F24
  shadowDark: 'rgba(33, 92, 158, 0.23)', // #215C9E
  shadowLightDark: 'rgba(42, 96, 159, 0.15)', // #2A609F
  skeletonBaseDark: '#202020', // #202020
  skeletonHighlightDark: '#444', // #444
  dropZoneErrBgDark: '#4a2c2c', // #4a2c2c
  dropZoneErrBorderDark: '#cc4444', // #cc4444
  statusGreenDark: '#22A588', // #22A588
  statusAmberDark: '#FFA800', // #FFA800
  statusRedDark: '#E13D3D', // #E13D3D
  statusOrangeDark: '#D08A00', // slightly muted/darker variant of #ED9C00 for dark bg

  // Org hierarchy avatar colors
  orgAvatarRoot: '#022033', // Root level org
  orgAvatarParent: '#E52D7A', // Parent org (first level under root)
  orgAvatarSub: '#FF5A28', // Sub org (deeper levels)
  orgAvatarWorkspace: '#15A5D6', // Workspace
  // Dark mode variants
  orgAvatarRootDark: '#4A6A7A', // Lighter for dark mode visibility
  orgAvatarParentDark: '#E52D7A',
  orgAvatarSubDark: '#FF5A28',
  orgAvatarWorkspaceDark: '#15A5D6',

  // Org table child row highlight
  childRowHighlight: '#F9FAFB', // Light mode - subtle gray
  childRowHighlightDark: '#1F2937', // Dark mode - subtle dark gray

  // ===== Card system (CRPD-5424, Figma 17757-4640) =====
  // Light values are the design's own tokens, verbatim. Dark values keep each
  // token's RELATIONSHIP to its neighbours (a border stays a shade off the
  // surface; muted text stays between primary and placeholder) rather than
  // being inverted numerically, matching how the rest of this palette is built.
  borderDefault: '#e6e9ef', // Figma border/default — card + chip outlines
  borderSubtle: '#f0f2f6', // Figma border/subtle — dividers inside a card
  surfaceSubtle: '#fcfcfd', // Figma surface/subtle — card footer / related block
  textMuted: '#6b7280', // Figma text/muted — counts, meta
  textStrong: '#10192b', // Figma text/primary — related framework names
  textSecondary: '#38435a', // Figma text/secondary — control-id chips
  textPlaceholder: '#98a1b0', // Figma text/placeholder — "N controls"
  sourceAccent: '#3b6fe0', // Figma source/1 — swatch tying related rows to their source

  borderDefaultDark: '#3E4560',
  borderSubtleDark: '#33384D',
  surfaceSubtleDark: '#2E2E30',
  textMutedDark: '#8B93A5',
  textStrongDark: '#C9D6E2',
  textSecondaryDark: '#A7B2C4',
  textPlaceholderDark: '#6E7789',
  sourceAccentDark: '#6D93EC',
  // Content-pack status chips. Light values are the literals that were inlined in
  // ContentManagementPage.styles.ts; the dark values are new — the chips previously had none, which
  // is why a near-white pill sat on a dark card under dark-mode text.
  chipPartnerBorder: '#8CCBFF',
  chipPartnerBorderDark: '#2F6B9E',
  chipPartnerBg: '#F2FAFF',
  chipPartnerBgDark: '#16283A',
  chipPartnerText: '#237AB8',
  chipPartnerTextDark: '#8CCBFF',
  chipPublishedBorder: '#77DDC4',
  chipPublishedBorderDark: '#2E6E5E',
  chipPublishedBg: '#F1FFFB',
  chipPublishedBgDark: '#132A24',
  chipPublishedText: '#00A886',
  chipPublishedTextDark: '#77DDC4',
};

export const colorsApp: { light: ColorSet; dark: ColorSet } = {
  light: {
    gp1: colorLib.gp1,
    gp2: colorLib.gp2,
    gradient: colorLib.gradient,
    gradientVertical: colorLib.gradientVertical,
    gradientBlue: colorLib.gradientBlue,
    hoverGradient: colorLib.hoverGradient,
    btnWhite: colorLib.btnWhite,
    prussianBlue: colorLib.prussianBlue,
    davysGrey: colorLib.davysGrey,
    aliceBlue: colorLib.aliceBlue,
    brightBlue: colorLib.brightBlue,
    cultured: colorLib.cultured,
    stroke: colorLib.stroke,
    cflowerBlue: colorLib.cflowerBlue,
    white: colorLib.white,
    warning: colorLib.warning,
    error: colorLib.error,
    compareAFill: colorLib.compareAFill,
    compareBLine: colorLib.compareBLine,
    compareBFill: colorLib.compareBFill,
    success: colorLib.success,
    bgGray: colorLib.bgGray,
    compareA: colorLib.compareA,
    compareB: colorLib.compareB,
    shadow: 'rgba(7, 46, 91, 0.23)',
    shadowLight: 'rgba(7, 46, 91, 0.15)',
    skeletonBase: colorLib.skeletonBase,
    skeletonHighlight: colorLib.skeletonHighlight,
    dropZoneErrBg: colorLib.dropZoneErrBg,
    dropZoneErrBorder: colorLib.dropZoneErrBorder,
    statusGreen: colorLib.statusGreen,
    statusAmber: colorLib.statusAmber,
    statusRed: colorLib.statusRed,
    statusOrange: colorLib.statusOrange,
    orgAvatarRoot: colorLib.orgAvatarRoot,
    orgAvatarParent: colorLib.orgAvatarParent,
    orgAvatarSub: colorLib.orgAvatarSub,
    orgAvatarWorkspace: colorLib.orgAvatarWorkspace,
    childRowHighlight: colorLib.childRowHighlight,
    borderDefault: colorLib.borderDefault,
    borderSubtle: colorLib.borderSubtle,
    surfaceSubtle: colorLib.surfaceSubtle,
    textMuted: colorLib.textMuted,
    textStrong: colorLib.textStrong,
    textSecondary: colorLib.textSecondary,
    textPlaceholder: colorLib.textPlaceholder,
    sourceAccent: colorLib.sourceAccent,
    scrim: 'rgba(2, 32, 51, 0.25)',
    chipPartnerBorder: colorLib.chipPartnerBorder,
    chipPartnerBg: colorLib.chipPartnerBg,
    chipPartnerText: colorLib.chipPartnerText,
    chipPublishedBorder: colorLib.chipPublishedBorder,
    chipPublishedBg: colorLib.chipPublishedBg,
    chipPublishedText: colorLib.chipPublishedText,
  },
  dark: {
    gp1: colorLib.gp1Dark,
    gp2: colorLib.gp2Dark,
    gradient: colorLib.gradientDark,
    gradientVertical: colorLib.gradientVerticalDark,
    gradientBlue: colorLib.gradientBlueDark,
    hoverGradient: colorLib.hoverGradientDark,
    btnWhite: colorLib.btnWhiteDark,
    prussianBlue: colorLib.prussianBlueDark,
    davysGrey: colorLib.davysGreyDark,
    aliceBlue: colorLib.aliceBlueDark,
    brightBlue: colorLib.brightBlueDark,
    cultured: colorLib.culturedDark,
    stroke: colorLib.strokeDark,
    cflowerBlue: colorLib.cflowerBlueDark,
    white: colorLib.whiteDark,
    warning: colorLib.warningDark,
    error: colorLib.errorDark,
    compareAFill: colorLib.compareAFillDark,
    compareBLine: colorLib.compareBLineDark,
    compareBFill: colorLib.compareBFillDark,
    success: colorLib.successDark,
    bgGray: colorLib.bgGrayDark,
    compareA: colorLib.compareADark,
    compareB: colorLib.compareBDark,
    shadow: colorLib.shadowDark,
    shadowLight: colorLib.shadowLightDark,
    skeletonBase: colorLib.skeletonBaseDark,
    skeletonHighlight: colorLib.skeletonHighlightDark,
    dropZoneErrBg: colorLib.dropZoneErrBgDark,
    dropZoneErrBorder: colorLib.dropZoneErrBorderDark,
    statusGreen: colorLib.statusGreenDark,
    statusAmber: colorLib.statusAmberDark,
    statusRed: colorLib.statusRedDark,
    statusOrange: colorLib.statusOrangeDark,
    orgAvatarRoot: colorLib.orgAvatarRootDark,
    orgAvatarParent: colorLib.orgAvatarParentDark,
    orgAvatarSub: colorLib.orgAvatarSubDark,
    orgAvatarWorkspace: colorLib.orgAvatarWorkspaceDark,
    childRowHighlight: colorLib.childRowHighlightDark,
    borderDefault: colorLib.borderDefaultDark,
    borderSubtle: colorLib.borderSubtleDark,
    surfaceSubtle: colorLib.surfaceSubtleDark,
    textMuted: colorLib.textMutedDark,
    textStrong: colorLib.textStrongDark,
    textSecondary: colorLib.textSecondaryDark,
    textPlaceholder: colorLib.textPlaceholderDark,
    sourceAccent: colorLib.sourceAccentDark,
    scrim: 'rgba(0, 0, 0, 0.6)',
    chipPartnerBorder: colorLib.chipPartnerBorderDark,
    chipPartnerBg: colorLib.chipPartnerBgDark,
    chipPartnerText: colorLib.chipPartnerTextDark,
    chipPublishedBorder: colorLib.chipPublishedBorderDark,
    chipPublishedBg: colorLib.chipPublishedBgDark,
    chipPublishedText: colorLib.chipPublishedTextDark,
  },
};

// ---------------------------------------------------------------------------
// Current-scheme color access for non-React code (chart configs, audit
// renderers). Kept in sync by ThemeSchemeProvider on every scheme change.
// React components should use styled-components' useTheme() instead.
// ---------------------------------------------------------------------------

let currentColors: ColorSet = colorsApp.light;

export const setCurrentColors = (colors: ColorSet): void => {
  currentColors = colors;
};

export const getCurrentColors = (): ColorSet => currentColors;
